/**
 * Error Handler Utility
 * Provides safe error handling and sanitized error messages for production
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Sanitize error message for user display
 * @param {Error|string} error - Error object or message
 * @returns {string} Sanitized error message
 */
export const sanitizeErrorMessage = (error) => {
  if (!error) return 'An unexpected error occurred. Please try again.';
  
  // If it's a string, use it directly (but sanitize)
  if (typeof error === 'string') {
    return sanitizeErrorString(error);
  }
  
  // If it's an Error object
  if (error instanceof Error) {
    // In development, show more details
    if (isDevelopment) {
      return error.message || 'An error occurred';
    }
    
    // In production, use generic messages based on error type
    const message = error.message || '';
    
    // Firebase Auth errors
    if (message.includes('auth/')) {
      return getFirebaseAuthErrorMessage(message);
    }
    
    // Firebase Firestore errors
    if (message.includes('firestore/') || message.includes('permission-denied')) {
      return 'You do not have permission to perform this action.';
    }
    
    // Network errors
    if (message.includes('network') || message.includes('fetch')) {
      return 'Network error. Please check your connection and try again.';
    }
    
    // Generic error
    return 'An error occurred. Please try again.';
  }
  
  // Fallback
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Sanitize error string to remove sensitive information
 * @param {string} errorString - Error string
 * @returns {string} Sanitized string
 */
const sanitizeErrorString = (errorString) => {
  if (!errorString || typeof errorString !== 'string') {
    return 'An unexpected error occurred. Please try again.';
  }
  
  // Remove file paths
  let sanitized = errorString.replace(/\/[^\s]+/g, '[path]');
  
  // Remove email addresses (but keep domain)
  sanitized = sanitized.replace(/\b[\w.-]+@[\w.-]+\.\w+\b/g, '[email]');
  
  // Remove potential tokens/keys
  sanitized = sanitized.replace(/\b[A-Za-z0-9]{32,}\b/g, '[token]');
  
  // Truncate if too long
  if (sanitized.length > 200) {
    sanitized = sanitized.substring(0, 200) + '...';
  }
  
  return sanitized;
};

/**
 * Get user-friendly Firebase Auth error message
 * @param {string} errorCode - Firebase error code
 * @returns {string} User-friendly message
 */
const getFirebaseAuthErrorMessage = (errorCode) => {
  const errorMessages = {
    'auth/user-not-found': 'No account found with this email address.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/email-already-in-use': 'This email is already registered.',
    'auth/weak-password': 'Password is too weak. Please use a stronger password.',
    'auth/invalid-email': 'Invalid email address.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/requires-recent-login': 'Please log out and log back in to continue.',
    'auth/operation-not-allowed': 'This operation is not allowed.',
  };
  
  // Try to match error code
  for (const [code, message] of Object.entries(errorMessages)) {
    if (errorCode.includes(code)) {
      return message;
    }
  }
  
  // Generic auth error
  return 'Authentication error. Please try again.';
};

/**
 * Log error to console (development) or error tracking service (production)
 * @param {Error} error - Error object
 * @param {string} context - Context where error occurred
 * @param {object} metadata - Additional metadata
 */
export const logError = (error, context = 'Unknown', metadata = {}) => {
  // In development, log full error details
  if (isDevelopment) {
    console.error(`[ERROR] ${context}:`, error);
    if (Object.keys(metadata).length > 0) {
      console.error('Metadata:', metadata);
    }
    if (error?.stack) {
      console.error('Stack trace:', error.stack);
    }
    return;
  }
  
  // In production, log sanitized error
  const sanitizedError = {
    message: error?.message || 'Unknown error',
    name: error?.name || 'Error',
    context,
    metadata: sanitizeMetadata(metadata),
    timestamp: new Date().toISOString(),
  };
  
  // Log to console (will be picked up by error tracking service if configured)
  console.error('[ERROR]', sanitizedError);
  
  // TODO: Send to error tracking service (e.g., Sentry, LogRocket)
  // Example: errorTrackingService.captureException(error, { context, metadata });
};

/**
 * Sanitize metadata for logging
 * @param {object} metadata - Metadata to sanitize
 * @returns {object} Sanitized metadata
 */
const sanitizeMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object') return {};
  
  const sensitiveKeys = ['password', 'token', 'secret', 'key', 'apiKey'];
  const sanitized = { ...metadata };
  
  Object.keys(sanitized).forEach((key) => {
    const lowerKey = key.toLowerCase();
    if (sensitiveKeys.some(sk => lowerKey.includes(sk))) {
      sanitized[key] = '[REDACTED]';
    }
  });
  
  return sanitized;
};

/**
 * Handle error with user notification
 * @param {Error|string} error - Error to handle
 * @param {string} context - Context where error occurred
 * @param {function} showToast - Toast notification function (optional)
 * @returns {string} Sanitized error message
 */
export const handleError = (error, context = 'Unknown', showToast = null) => {
  const sanitizedMessage = sanitizeErrorMessage(error);
  
  // Log error
  if (error instanceof Error) {
    logError(error, context);
  } else {
    console.error(`[ERROR] ${context}:`, error);
  }
  
  // Show toast if provided
  if (showToast && typeof showToast === 'function') {
    showToast(sanitizedMessage, { type: 'error' });
  }
  
  return sanitizedMessage;
};

